import {useState} from 'react';
import Delete from '@mui/icons-material/Delete';
import {Box, Button, IconButton, TextField} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  CountryCode,
  useAddCountryCode,
  useDeleteCountryCode,
  useGetCountryCodes,
} from '@entities/country';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {Header} from '@widgets/common';

import {COLOR__WHITE} from '@/theme/colors';

export const CountriesPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const {data = [], isPending, error} = useGetCountryCodes();
  const {mutate: addCountryCode, isPending: isAdding} = useAddCountryCode();
  const {mutate: deleteCountryCode} = useDeleteCountryCode();

  const [inputCode, setInputCode] = useState('');

  const handleAdd = () => {
    const code = inputCode.trim().toUpperCase();
    if (!code) return;

    addCountryCode(code, {
      onSuccess: () => {
        enqueueSnackbar(`Country ${code} added`, {variant: 'success'});
        setInputCode('');
      },
      onError: err => {
        const msg = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
        enqueueSnackbar(`Could not add country. ${msg}`, {variant: 'error'});
      },
    });
  };

  const handleDelete = async (countryCode: string) => {
    try {
      await openConfirmation({
        title: 'Remove country',
        content: `Remove ${countryCode} from whitelist?`,
        okButtonText: 'Remove',
      });
    } catch {
      return;
    }

    deleteCountryCode(countryCode, {
      onSuccess: () =>
        enqueueSnackbar(`Country ${countryCode} removed`, {variant: 'success'}),
      onError: err => {
        const msg = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
        enqueueSnackbar(`Could not remove country. ${msg}`, {variant: 'error'});
      },
    });
  };

  const columns: MRT_ColumnDef<CountryCode>[] = [
    {
      accessorKey: 'countryCode',
      header: 'Country code',
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      Cell: ({row}) => {
        const date = row.original.createdAt;
        if (!date) return null;
        return formatInTz(new Date(date), DATETIME_FORMAT);
      },
    },
  ];

  return (
    <>
      <Header title="Countries whitelist" mb={1} />

      <Box bgcolor={COLOR__WHITE} borderRadius={2} p={3}>
        <MrTable
          error={error}
          columns={columns}
          data={data}
          renderTopToolbarCustomActions={() => (
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                size="small"
                placeholder="Country code (e.g. US)"
                value={inputCode}
                onChange={e => setInputCode(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAdd();
                }}
                sx={{width: 220}}
              />
              <Button
                onClick={handleAdd}
                disabled={isAdding || !inputCode.trim()}
              >
                Add country
              </Button>
            </Box>
          )}
          renderRowActions={({row}) => (
            <TableRowActionsContainer>
              <IconButton
                size="small"
                onClick={() => void handleDelete(row.original.countryCode)}
              >
                <Delete />
              </IconButton>
            </TableRowActionsContainer>
          )}
          state={{isLoading: isPending}}
          {...entityTableCommonProps}
        />
      </Box>
    </>
  );
};
